/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useRef } from "preact/hooks";
import { clamp } from "@utils/utils";
import { ParamDict, ParamDictToProps } from "@utils/params";

//#region AdsrEnvelopeParams
export const AdsrEnvelopeParams = {
  "domain/maxTime": { type: "float", value: 4 },
  "domain/attackT": { type: "float", value: 0.3 },
  "domain/attackV": { type: "float", value: 1.0 },
  "domain/attackRamp": { type: "string", value: "exponential" },
  "domain/decayT": { type: "float", value: 0.8 },
  "domain/decayV": { type: "float", value: 0.6 },
  "domain/decayRamp": { type: "string", value: "exponential" },
  "domain/sustainT": { type: "float", value: 2.5 },
  "domain/releaseT": { type: "float", value: 3.5 },
  "domain/releaseRamp": { type: "string", value: "exponential" },
} satisfies ParamDict;
export type AdsrEnvelopeParams = typeof AdsrEnvelopeParams;
export const AdsrEnvelopeOptsDefaults = ParamDictToProps(AdsrEnvelopeParams);
export type AdsrEnvelopeOpts = typeof AdsrEnvelopeOptsDefaults;
//#endregion

//#region AdsrUiParams
export const AdsrUiParams = {
  "svg/width": { type: "int", value: 240 },
  "svg/height": { type: "int", value: 100 },
  "svg/pad": { type: "int", value: 14 },
} satisfies ParamDict;
export type AdsrUiParams = typeof AdsrUiParams;
export const AdsrUiOptsDefaults = ParamDictToProps(AdsrUiParams);
export type AdsrUiOpts = typeof AdsrUiOptsDefaults;
//#endregion

// #region Types

export type AdsrRamp = "linear" | "exponential";

export interface AdsrEnvelope {
  /** Total display time range in seconds */
  maxTime: number;
  /** Attack phase end point — peak time and value */
  attack: { t: number; v: number; ramp: AdsrRamp };
  /** Decay phase end point — sustain time and sustain level */
  decay: { t: number; v: number; ramp: AdsrRamp };
  /** Sustain hold end — time only (value is inherited from decay.v) */
  sustain: { t: number; v?: number; ramp?: AdsrRamp };
  /** Release phase end — time only (value is always 0) */
  release: { t: number; ramp: AdsrRamp };
}

export const AdsrEnvelopeDefault: AdsrEnvelope = {
  maxTime: AdsrEnvelopeOptsDefaults["domain/maxTime"],
  attack: {
    t: AdsrEnvelopeOptsDefaults["domain/attackT"],
    v: AdsrEnvelopeOptsDefaults["domain/attackV"],
    ramp: AdsrEnvelopeOptsDefaults["domain/attackRamp"] as AdsrRamp,
  },
  decay: {
    t: AdsrEnvelopeOptsDefaults["domain/decayT"],
    v: AdsrEnvelopeOptsDefaults["domain/decayV"],
    ramp: AdsrEnvelopeOptsDefaults["domain/decayRamp"] as AdsrRamp,
  },
  sustain: { t: AdsrEnvelopeOptsDefaults["domain/sustainT"] },
  release: {
    t: AdsrEnvelopeOptsDefaults["domain/releaseT"],
    ramp: AdsrEnvelopeOptsDefaults["domain/releaseRamp"] as AdsrRamp,
  },
};

// #endregion

// #region buildEnvelopePath

/**
 * Build an SVG path string for the ADSR envelope.
 * Exponential ramps use quadratic Béziers approximating the WebAudio exponential
 * curve shape (slow-start/fast-end for attack; fast-start/slow-end for decay/release).
 * Linear ramps use straight line segments.
 */
export function buildEnvelopePath(
  v: AdsrEnvelope,
  tx: (t: number) => number,
  vy: (val: number) => number,
): string {
  const x0 = tx(0), y0 = vy(0);
  const xa = tx(v.attack.t), ya = vy(v.attack.v);
  const xd = tx(v.decay.t), yd = vy(v.decay.v);
  const xs = tx(v.sustain.t); // ys = yd (sustain level)
  const ys = v.sustain.v === undefined ? yd : vy(v.sustain.v);
  const xr = tx(v.release.t), yr = vy(0);

  // Attack: 0 → peak — exponential = slow start / fast end → control near (xa, y0)
  const attackSeg = v.attack.ramp === "exponential" ? `Q ${xa},${y0} ${xa},${ya}` : `L ${xa},${ya}`;

  // Decay: peak → sustain — exponential = fast drop / slow end → control near (xa, yd)
  const decaySeg = v.decay.ramp === "exponential" ? `Q ${xa},${yd} ${xd},${yd}` : `L ${xd},${yd}`;

  // Sustain: flat hold (always linear)
  const sustainSeg = v.sustain.ramp === "exponential"
    ? `Q ${xd},${ys} ${xs},${ys}`
    : `L ${xs},${ys}`;

  // Release: sustain → 0 — exponential = fast drop / slow end → control near (xs, yr)
  const releaseSeg = v.release.ramp === "exponential"
    ? `Q ${xs},${yr} ${xr},${yr}`
    : `L ${xr},${yr}`;

  return `M ${x0},${y0} ${attackSeg} ${decaySeg} ${sustainSeg} ${releaseSeg}`;
}

// #endregion

// #region AdsrEditor

type DragTarget = "attack" | "decay" | "sustain" | "release";

export interface AdsrEditorProps {
  value: AdsrEnvelope;
  onChange: (v: AdsrEnvelope) => void;
  opts?: Partial<AdsrUiOpts>;
}

export function AdsrEditor(props: AdsrEditorProps) {
  const opts = { ...AdsrUiOptsDefaults, ...props.opts };
  const adsrWidth = opts["svg/width"];
  const adsrHeight = opts["svg/height"];
  const adsrPad = opts["svg/pad"];
  const value = props.value;
  const onChange = props.onChange;

  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef<DragTarget | null>(null);

  // Coordinate mappers: domain → SVG pixels
  const tx = (t: number) => adsrPad + (t / value.maxTime) * (adsrWidth - adsrPad * 2);
  const vy = (val: number) => adsrPad + (1 - val) * (adsrHeight - adsrPad * 2);

  // Inverse: SVG pixels → domain
  const xt = (x: number) =>
    clamp(((x - adsrPad) / (adsrWidth - adsrPad * 2)) * value.maxTime, 0, value.maxTime);
  const yv = (y: number) => clamp(1 - (y - adsrPad) / (adsrHeight - adsrPad * 2), 0, 1);

  const getSvgPos = (e: MouseEvent): { t: number; v: number } => {
    const svg = svgRef.current;
    if (!svg) return { t: 0, v: 0 };
    const rect = svg.getBoundingClientRect();
    return { t: xt(e.clientX - rect.left), v: yv(e.clientY - rect.top) };
  };

  const onMouseMove = (e: MouseEvent) => {
    const target = dragging.current;
    if (!target) return;
    const { t, v } = getSvgPos(e);

    const next = { ...value };

    switch (target) {
      case "attack": {
        const tB = clamp(t, 0.001, value.decay.t - 0.001);
        next.attack = { ...value.attack, t: tB, v: clamp(v, 0, 1) };
        break;
      }
      case "decay": {
        const tB = clamp(t, value.attack.t + 0.001, value.sustain.t - 0.001);
        next.decay = { ...value.decay, t: tB, v: clamp(v, 0, 1) };
        break;
      }
      case "sustain": {
        // slides both axes — v change propagates to decay.v (sustain level)
        const tB = clamp(t, value.decay.t + 0.001, value.release.t - 0.001);
        if (value.sustain.v === undefined) {
          next.sustain = { t: tB };
          next.decay = { ...value.decay, v: clamp(v, 0, 1) };
        } else {
          next.sustain = { ...value.sustain, t: tB, v: clamp(v, 0, 1) };
        }
        break;
      }
      case "release": {
        // release end slides horizontally only — value is locked to 0
        const tB = clamp(t, value.sustain.t + 0.001, value.maxTime);
        next.release = { ...value.release, t: tB };
        break;
      }
    }
    onChange(next);
  };

  const onMouseUp = () => {
    dragging.current = null;
  };

  const startDrag = (target: DragTarget) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = target;
  };

  // Precompute point coordinates
  const pts = {
    attack: [tx(value.attack.t), vy(value.attack.v)],
    decay: [tx(value.decay.t), vy(value.decay.v)],
    sustain: [tx(value.sustain.t), vy(value.sustain.v ?? value.decay.v)], // y = sustain level = decay.v
    release: [tx(value.release.t), vy(0)],
  } as Record<string, number[]>;

  const envelopePath = buildEnvelopePath(value, tx, vy);

  // Fill uses straight lines (close approximation is fine for the fill area)
  const fillPath = [
    `M ${tx(0)},${vy(0)}`,
    `L ${pts.attack.join(",")}`,
    `L ${pts.decay.join(",")}`,
    `L ${pts.sustain.join(",")}`,
    `L ${pts.release.join(",")}`,
    `L ${tx(value.release.t)},${vy(-0.05)}`,
    `L ${tx(0)},${vy(-0.05)} Z`,
  ].join(" ");

  const draggablePoints: [DragTarget, number[]][] = [
    ["attack", pts.attack],
    ["decay", pts.decay],
    ["sustain", pts.sustain],
    ["release", pts.release],
  ];

  const pointLabels: [string, number[]][] = [
    ["A", pts.attack],
    ["D", pts.decay],
    ["S", pts.sustain],
    ["R", pts.release],
  ];

  return (
    <div style={{ padding: "6px 4px 8px" }}>
      <svg
        ref={svgRef}
        width={adsrWidth}
        height={adsrHeight}
        style={{ display: "block", userSelect: "none" }}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Background */}
        <rect x={0} y={0} width={adsrWidth} height={adsrHeight} fill="#1a1a1a" rx={3} />

        {/* Axis guides */}
        <line
          x1={adsrPad}
          y1={vy(0)}
          x2={adsrWidth - adsrPad}
          y2={vy(0)}
          stroke="#333"
          strokeWidth={1}
        />
        <line
          x1={adsrPad}
          y1={adsrPad}
          x2={adsrPad}
          y2={vy(0)}
          stroke="#333"
          strokeWidth={1}
        />

        {/* Fill under envelope */}
        <path d={fillPath} fill="rgba(68,170,255,0.1)" />

        {/* Envelope path (curved for exponential ramps) */}
        <path
          d={envelopePath}
          fill="none"
          stroke="#4af"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />

        {/* Point labels */}
        {pointLabels.map(([label, [lx, ly]]) => (
          <text
            key={label}
            x={lx}
            y={ly - 8}
            textAnchor="middle"
            fill="#888"
            fontSize={9}
            style={{ pointerEvents: "none" }}
          >
            {label}
          </text>
        ))}

        {/* Draggable point handles */}
        {draggablePoints.map(([name, [cx, cy]]) => (
          <circle
            key={name}
            cx={cx}
            cy={cy}
            r={5}
            fill="#222"
            stroke="#4af"
            strokeWidth={1.5}
            style={{ cursor: "grab" }}
            onMouseDown={startDrag(name)}
          />
        ))}
      </svg>

      {/* Ramp type dropdowns — one per ramp segment */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: 5,
          paddingLeft: adsrPad,
        }}
      >
        {(value.sustain.ramp === undefined
          ? (["attack", "decay", "release"] as const)
          : (["attack", "decay", "sustain", "release"] as const)).map((stage) => (
            <label
              key={stage}
              style={{
                fontSize: 10,
                color: "#888",
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
            >
              {stage.charAt(0).toUpperCase()}:
              <select
                value={value[stage].ramp ?? "linear"}
                style={{
                  fontSize: 10,
                  background: "#222",
                  color: "#ccc",
                  border: "1px solid #444",
                  borderRadius: 2,
                  padding: "1px 2px",
                }}
                onChange={(e) => {
                  const ramp = (e.target as HTMLSelectElement).value as AdsrRamp;
                  onChange({ ...value, [stage]: { ...value[stage], ramp } });
                }}
              >
                <option value="linear">linear</option>
                <option value="exponential">exp</option>
              </select>
            </label>
          ))}
      </div>
    </div>
  );
}

// #endregion

// #region AdsrInline

export interface AdsrInlineProps {
  value: AdsrEnvelope;
}

export function AdsrInline(props: AdsrInlineProps) {
  const inlineWidth = 80;
  const inlineHeight = 18;
  const tx = (t: number) => (t / props.value.maxTime) * inlineWidth;
  const vy = (val: number) => (1 - val) * inlineHeight;
  const envelopePath = buildEnvelopePath(props.value, tx, vy);

  return (
    <span style={{ flexGrow: 1, overflow: "hidden", flexShrink: 0 }}>
      <svg width={inlineWidth} height={inlineHeight} style={{ display: "block" }}>
        <path d={envelopePath} fill="none" stroke="#4af" strokeWidth={1} strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// #endregion
